import { API_ENDPOINTS } from '../config/api'

export type ControlOverviewRow = {
  controlKey: string
  title: string
  description?: string
  questionCount: number
  questionsAnswered: number
  primaryAnswered?: boolean
  evidenceSufficiency?: string
  primaryQuestionKey?: string | null
  controlStatus?: string
}

export type ControlCentricProgress = {
  controlsTotal: number
  controlsCompleted: number
  controlsEvidenceSupported: number
  controlsWithGaps: number
  partialControls: number
  nonCompliantControls: number
  progressLabel: string
  evidenceRepositorySummary?: {
    totalArtifacts: number
    validArtifacts: number
    unverifiedArtifacts: number
  }
  advanced?: {
    operationalAssertionsCompleted: number
    operationalAssertionsTotal: number
    hiddenAssertions: number
    secondaryAssertions: number
  }
}

export type ControlCentricOverview = {
  enabled: boolean
  session?: {
    id: string
    standardCode: string
    updatedAt: string
    stage: string
    status?: string
    stageSubtitle?: string | null
  }
  progress?: ControlCentricProgress & {
    controlsProgressing?: number
    label?: string
  }
  stage?: { stageKey: string; displayStage: string; subtitle?: string | null; canFinish?: boolean }
  controls?: ControlOverviewRow[]
}

export type ControlCentricCompletionSummary = {
  title: string
  controlsCompleted: number
  controlsTotal: number
  controlsEvidenceSupported: number
  controlsWithGaps: number
  partialControls: number
  nonCompliantControls: number
  evidenceRepositorySummary?: ControlCentricProgress['evidenceRepositorySummary']
  subtitle?: string | null
  nextActions: string[]
}

export type ControlExecutionDetail = {
  enabled: boolean
  controlKey?: string
  title?: string
  description?: string
  soc2Criteria?: string | null
  criteriaReference?: string | null
  standardCode?: string
  evidenceSufficiency?: string
  owners?: Record<string, string | null>
  legacyControlCode?: string | null
  questions?: Array<{
    questionKey: string
    domainKey?: string
    legacyControlCode?: string
    localization: { assertion: { en?: string; ru?: string; es?: string }; description?: { en?: string } }
    trustCriteria?: string[]
    answerValue?: string | null
    answered?: boolean
    assessorNote?: string | null
  }>
  linkedEvidence?: Array<{ evidenceId: string; title?: string; verificationStatus?: string; freshnessStatus?: string }>
  evidenceExpectations?: Array<{ requirementKey: string; title: string }>
  expectedEvidenceHint?: string | null
  recommendation?: { localization: { en?: string; ru?: string; es?: string } }
  remediation?: { localization: { en?: string; ru?: string; es?: string } }
  optionalGuidance?: unknown[]
}

export async function fetchControlCentricOverview(
  token: string,
  sessionId: string,
  standardCode?: string,
  lang?: string,
): Promise<ControlCentricOverview> {
  const params = new URLSearchParams()
  if (standardCode) params.set('standardCode', standardCode)
  if (lang) params.set('lang', lang)
  const q = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`${API_ENDPOINTS.COMPLIANCE_CONTROL_CENTRIC_OVERVIEW(sessionId)}${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Overview ${res.status}`)
  return json?.data || { enabled: false }
}

export async function fetchControlExecutionDetail(
  token: string,
  sessionId: string,
  controlKey: string,
  lang?: string,
): Promise<ControlExecutionDetail> {
  const params = new URLSearchParams()
  if (lang) params.set('lang', lang)
  const q = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`${API_ENDPOINTS.COMPLIANCE_CONTROL_CENTRIC_CONTROL(sessionId, controlKey)}${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Control ${res.status}`)
  return json?.data || { enabled: false }
}

export async function finishControlCentricSession(
  token: string,
  sessionId: string,
): Promise<{ progress: ControlCentricProgress; completionSummary: ControlCentricCompletionSummary; stage: unknown }> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSION_FINISH(sessionId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Finish ${res.status}`)
  return json?.data
}

export async function saveControlOwners(
  token: string,
  sessionId: string,
  controlKey: string,
  owners: Record<string, string | null | undefined>,
): Promise<unknown> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_CONTROL_CENTRIC_OWNERS(sessionId, controlKey), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(owners),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Save owners ${res.status}`)
  return json?.data
}
